/**
 * 默认设置初始化器
 * 确保应用启动时有正确的默认配置
 */

export class DefaultSettingsInitializer {
  /**
   * 初始化默认设置
   */
  static initializeDefaultSettings() {
    console.log('🔧 检查并初始化默认设置...')
    
    try {
      // 检查是否已有设置
      const existingSettings = localStorage.getItem('altlife-settings')
      
      if (!existingSettings) {
        console.log('📝 未找到现有设置，创建默认配置...')
        this.createDefaultSettings()
      } else {
        console.log('✅ 发现现有设置，验证完整性...')
        this.validateAndFixSettings(JSON.parse(existingSettings))
      }
    } catch (error) {
      console.error('❌ 设置初始化失败:', error)
      console.log('🔄 创建全新的默认设置...')
      this.createDefaultSettings()
    }
  }
  
  /**
   * 创建默认设置
   */
  static createDefaultSettings() {
    const defaultSettings = {
      // 语言模型配置
      languageModel: 'gemini-2.5-pro',
      languageApiKey: '',
      languageApiUrl: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-pro:generateContent',
      
      // 图像模型配置
      imageModel: 'doubao-seedream-4-0-250828',
      imageApiKey: '',
      imageApiUrl: 'http://localhost:3003/api/doubao',
      
      // 应用设置
      autoSave: true,
      darkMode: true,
      language: 'zh-CN',
      
      // 版本信息
      version: '1.1.0',
      createdAt: new Date().toISOString(),
      
      // 功能开关
      enableImageGeneration: true,
      enableAutoImageProxy: true,
      maxImageRetries: 2,
      imageLoadTimeout: 15000
    }
    
    try {
      localStorage.setItem('altlife-settings', JSON.stringify(defaultSettings))
      console.log('✅ 默认设置已创建并保存')
      
      // 显示初始化通知
      this.showInitializationNotification()
      
      return defaultSettings
    } catch (error) {
      console.error('❌ 保存默认设置失败:', error)
      return null
    }
  }
  
  /**
   * 验证并修复设置
   */
  static validateAndFixSettings(settings) {
    let needsSave = false
    const fixedSettings = { ...settings }
    
    // 检查必需字段
    const requiredFields = {
      languageModel: 'gemini-2.5-pro',
      languageApiUrl: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-pro:generateContent',
      imageModel: 'doubao-seedream-4-0-250828',
      imageApiUrl: 'http://localhost:3003/api/doubao',
      autoSave: true,
      darkMode: true,
      language: 'zh-CN'
    }
    
    for (const [key, defaultValue] of Object.entries(requiredFields)) {
      if (!(key in fixedSettings)) {
        console.log(`🔧 添加缺失字段: ${key} = ${defaultValue}`)
        fixedSettings[key] = defaultValue
        needsSave = true
      }
    }
    
    // 检查端口迁移
    if (fixedSettings.imageApiUrl && fixedSettings.imageApiUrl.includes('localhost:3001')) {
      console.log('🔄 迁移图片API端口从3001到3003')
      fixedSettings.imageApiUrl = fixedSettings.imageApiUrl.replace('localhost:3001', 'localhost:3003')
      needsSave = true
    }
    
    // 更新版本信息
    if (!fixedSettings.version || fixedSettings.version !== '1.1.0') {
      fixedSettings.version = '1.1.0'
      fixedSettings.updatedAt = new Date().toISOString()
      needsSave = true
    }
    
    // 保存修复后的设置
    if (needsSave) {
      try {
        localStorage.setItem('altlife-settings', JSON.stringify(fixedSettings))
        console.log('✅ 设置已验证并修复')
        this.showUpdateNotification()
      } catch (error) {
        console.error('❌ 保存修复设置失败:', error)
      }
    } else {
      console.log('✅ 设置验证通过，无需修复')
    }
    
    return fixedSettings
  }
  
  /**
   * 显示初始化通知
   */
  static showInitializationNotification() {
    this.showNotification(
      '🎉 欢迎使用 AltLife',
      '已为您创建默认配置。请在设置中配置您的API密钥以使用AI功能。',
      'success'
    )
  }
  
  /**
   * 显示更新通知
   */
  static showUpdateNotification() {
    this.showNotification(
      '🔄 设置已更新',
      '您的配置已自动更新到最新版本，图片生成功能已优化。',
      'info'
    )
  }
  
  /**
   * 显示通知
   */
  static showNotification(title, message, type = 'info') {
    // 创建通知元素
    const notification = document.createElement('div')
    
    const colors = {
      success: { bg: '#2d5a2d', border: '#4ade80', text: '#90ee90' },
      info: { bg: '#2d4a5a', border: '#60a5fa', text: '#87ceeb' },
      warning: { bg: '#5a5a2d', border: '#fbbf24', text: '#ffd700' },
      error: { bg: '#5a2d2d', border: '#f87171', text: '#ff6b6b' }
    }
    
    const color = colors[type] || colors.info
    
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${color.bg};
      border: 2px solid ${color.border};
      color: ${color.text};
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      z-index: 10000;
      max-width: 400px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      line-height: 1.5;
      animation: slideIn 0.3s ease-out;
    `
    
    notification.innerHTML = `
      <div style="display: flex; align-items: center; margin-bottom: 8px;">
        <strong>${title}</strong>
        <button onclick="this.parentElement.parentElement.remove()" style="
          background: none;
          border: none;
          color: ${color.text};
          font-size: 18px;
          cursor: pointer;
          margin-left: auto;
          padding: 0;
          width: 20px;
          height: 20px;
        ">×</button>
      </div>
      <div>${message}</div>
    `
    
    // 添加动画样式
    if (!document.getElementById('notification-styles')) {
      const style = document.createElement('style')
      style.id = 'notification-styles'
      style.textContent = `
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `
      document.head.appendChild(style)
    }
    
    document.body.appendChild(notification)
    
    // 8秒后自动消失
    setTimeout(() => {
      if (notification.parentElement) {
        notification.style.animation = 'slideIn 0.3s ease-out reverse'
        setTimeout(() => notification.remove(), 300)
      }
    }, 8000)
  }
  
  /**
   * 获取当前设置
   */
  static getCurrentSettings() {
    try {
      const settingsStr = localStorage.getItem('altlife-settings')
      return settingsStr ? JSON.parse(settingsStr) : null
    } catch (error) {
      console.error('❌ 读取设置失败:', error)
      return null
    }
  }
  
  /**
   * 检查设置健康状态
   */
  static checkSettingsHealth() {
    const settings = this.getCurrentSettings()
    
    if (!settings) {
      return { healthy: false, issues: ['设置不存在'] }
    }
    
    const issues = []
    
    // 检查API配置
    if (!settings.imageApiKey) {
      issues.push('未配置图片API密钥')
    }
    
    if (!settings.languageApiKey) {
      issues.push('未配置语言模型API密钥')
    }
    
    // 检查端口配置
    if (settings.imageApiUrl && settings.imageApiUrl.includes('localhost:3001')) {
      issues.push('图片API端口配置过时')
    }
    
    return {
      healthy: issues.length === 0,
      issues: issues,
      settings: settings
    }
  }
  
  /**
   * 快速设置向导
   */
  static showQuickSetupWizard() {
    const modal = document.createElement('div')
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 20000;
    `
    
    modal.innerHTML = `
      <div style="
        background: #2a2a2a;
        border-radius: 12px;
        padding: 30px;
        max-width: 500px;
        width: 90%;
        color: white;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      ">
        <h2 style="margin-top: 0; color: #4f46e5;">🚀 快速设置向导</h2>
        <p>欢迎使用 AltLife！为了获得最佳体验，请配置以下设置：</p>
        
        <div style="margin: 20px 0;">
          <h3>📝 必需配置：</h3>
          <ul style="margin: 10px 0; padding-left: 20px;">
            <li>图片生成API密钥（豆包）</li>
            <li>语言模型API密钥（Gemini）</li>
          </ul>
        </div>
        
        <div style="margin: 20px 0;">
          <h3>✅ 已自动配置：</h3>
          <ul style="margin: 10px 0; padding-left: 20px;">
            <li>图片代理服务器 (端口3003)</li>
            <li>智能图片加载策略</li>
            <li>本地存储和备份</li>
          </ul>
        </div>
        
        <div style="display: flex; gap: 10px; margin-top: 30px;">
          <button onclick="this.parentElement.parentElement.parentElement.remove()" style="
            flex: 1;
            background: #6b7280;
            color: white;
            border: none;
            padding: 12px;
            border-radius: 6px;
            cursor: pointer;
          ">稍后配置</button>
          <button onclick="window.location.hash='#settings'; this.parentElement.parentElement.parentElement.remove()" style="
            flex: 1;
            background: #4f46e5;
            color: white;
            border: none;
            padding: 12px;
            border-radius: 6px;
            cursor: pointer;
          ">立即配置</button>
        </div>
      </div>
    `
    
    document.body.appendChild(modal)
  }
}

export default DefaultSettingsInitializer
